import { useContext, useState, useEffect } from 'react';
import { useAxiosAuthenticated } from '../../../../useAxiosAuthenticated';
import { FileDownloadsContext } from '../../../../context/FileDownloadsContext';
import CounselingReportsHeader from '../../../Header/CounselingReportsHeader';
import ErrorCard from '../../../Cards/ErrorCard';
import { ERROR_TEXT } from '../../../../constants';
import StandardizedDataTable from '../../../PrimeAids/StandardizedDataTable';
import { Button } from '@mui/material';
import ExpandableInfoCard, {
    ExpandableInfoCardDescription,
} from '../../../Cards/ExpandableInfoCard';

const CounselingReports = () => {
    const [paramCobrand, setParamCobrand] = useState([]);
    const [inFlight, setInFlight] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [isRequestError, setIsRequestError] = useState(false);
    const [downloadedFiles, setDownloadedFiles] = useState(new Set());

    const { post, tokenError } = useAxiosAuthenticated();
    const { cobrandOptions } = useContext(FileDownloadsContext);

    useEffect(() => {
        if (cobrandOptions && cobrandOptions.length === 1) {
            setParamCobrand([cobrandOptions[0].id]);
        }
    }, [cobrandOptions]);

    const handleDownload = async (rowData) => {
        const fileKey = `${rowData.cobrandId}_${rowData.fileName}`;
        setDownloadedFiles((prev) => new Set(prev).add(fileKey));
        setTimeout(() => {
            setDownloadedFiles((prev) => {
                const next = new Set(prev);
                next.delete(fileKey);
                return next;
            });
        }, 3000);
        try {
            const url = `${process.env.REACT_APP_API_BASE_URL}/file-downloads/counseling-file-download`;
            const toSend = {
                CobrandId: rowData.cobrandId,
                FileName: rowData.fileName,
            };
            const resp = await post(url, toSend, {
                responseType: 'blob',
            });

            // Create a download link
            const blob = new Blob([resp.data]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = rowData.fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            // Optimistically reflect the download in the status column so the
            // user sees it as downloaded immediately, matching the persisted
            // status that the backend records and returns on the next list load.
            const today = new Date().toISOString().slice(0, 10);
            setReportData((prev) =>
                (prev || []).map((row) =>
                    row.cobrandId === rowData.cobrandId &&
                    row.fileName === rowData.fileName
                        ? { ...row, downloadedOn: today }
                        : row
                )
            );
        } catch (error) {
            console.error('Error downloading file:', error);
            setIsRequestError(true);
        }
    };

    const formatDownloadedOn = (value) => {
        if (!value) {
            return null;
        }
        const [year, month, day] = value.split('-');
        if (!year || !month || !day) {
            return value;
        }
        return `${month}/${day}/${year}`;
    };

    const columns = [
        { field: 'cobrandName', name: 'Cobrand Name', dataType: 'multiselect' },
        { field: 'documentType', name: 'Document Type', dataType: 'multiselect' },
        { field: 'date', name: 'Date', dataType: 'multiselect' },
        {
            field: 'downloadedOn',
            name: 'Downloaded',
            dataType: 'custom',
            body: (rowData) => {
                const formatted = formatDownloadedOn(rowData.downloadedOn);
                return formatted ? (
                    <span className="text-green-600">&#10003; {formatted}</span>
                ) : (
                    <span className="text-red-600">(not downloaded)</span>
                );
            },
        },
        {
            field: 'download',
            name: 'Download',
            dataType: 'custom',
            body: (rowData) => {
                const fileKey = `${rowData.cobrandId}_${rowData.fileName}`;
                const isDownloaded = downloadedFiles.has(fileKey);
                return (
                    <Button
                        onClick={() => handleDownload(rowData)}
                        variant="contained"
                        size="small"
                        disabled={isDownloaded}
                    >
                        Download
                    </Button>
                );
            },
        },
    ];

    const getReportData = async () => {
        setIsRequestError(false);
        setInFlight(true);
        setReportData(null);

        try {
            const url = `${process.env.REACT_APP_API_BASE_URL}/file-downloads/counseling-file-list`;
            const toSend = {
                CobrandIds: paramCobrand,
            };
            const resp = await post(url, toSend);
            const sorted = (resp?.data || []).sort(
                (a, b) => new Date(b.date) - new Date(a.date)
            );
            setReportData(sorted);
        } catch (error) {
            console.error('Error fetching counseling report data:', error);
            setIsRequestError(true);
        } finally {
            setInFlight(false);
        }
    };

    if (tokenError) return <ErrorCard>{ERROR_TEXT.token}</ErrorCard>;

    return (
        <div className="space-y-6">
            <CounselingReportsHeader
                paramCobrand={paramCobrand}
                setParamCobrand={setParamCobrand}
                cobrandOptions={cobrandOptions}
                getReportData={getReportData}
                inFlight={inFlight}
            />
            {isRequestError && <ErrorCard>{ERROR_TEXT.reportRequest}</ErrorCard>}
            {reportData && reportData.length === 0 && (
                <ErrorCard>No counseling reports available for the selected cobrands</ErrorCard>
            )}
            {reportData && reportData.length > 0 && (
                <>
                    <StandardizedDataTable data={reportData} columns={columns} />
                    <ExpandableInfoCard title="What is this data?">
                        <ExpandableInfoCardDescription>
                            This report shows Common Origination and Disbursement (COD)
                            counseling files available for download. Each row represents a
                            counseling file that has been processed and is ready for
                            download by cobrand.
                        </ExpandableInfoCardDescription>
                    </ExpandableInfoCard>
                </>
            )}
        </div>
    );
};

export default CounselingReports;
